import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Send, Flame, Sparkles, Zap, Check } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import ClientMarquee from "@/components/ClientMarquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const framework = [
  { pct: "30%", title: "Authority", icon: Sparkles, desc: "Deep-dive industry breakdowns, case studies and proprietary frameworks that establish expert status." },
  { pct: "30%", title: "Relatability", icon: Flame, desc: "Behind-the-scenes founder stories that build human connection and trust — the reason people follow, and stay." },
  { pct: "40%", title: "Virality", icon: Zap, desc: "Algorithm-engineered content hooks designed to expand top-of-funnel reach and get pushed by the feed." },
];

const HOURS = ["1-2", "2-3", "3-5", "5-8"];

export default function PersonalBrandingPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", handles: "", goal: "",
    hours_per_day: "", budget_ok: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#intake") {
      setTimeout(() => document.getElementById("intake")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email and phone are required.");
      return;
    }
    if (!form.hours_per_day) return toast.error("Please pick how many hours a day you can commit.");
    if (form.budget_ok === null) return toast.error("Please confirm the ₹75,000 investment.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/personal-branding/intake`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        handles: form.handles.trim() || null,
        goal: form.goal.trim() || null,
        hours_per_day: form.hours_per_day,
        budget_ok: form.budget_ok,
      });
      setSubmitted(true);
      toast.success("Application received — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-20 md:pt-20 md:pb-24">
          <Reveal>
            <div className="eyebrow mb-6">Personal Branding</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              Make your name <em className="gold-shine font-medium not-italic">unforgettable</em>.
              <br />
              <span className="text-zinc-300">Make your business </span>
              <span className="text-red-500">unmissable.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              In 2026, buyers don't trust faceless corporations — they trust founders. Build personal
              authority on YouTube and Instagram that compounds into revenue, out-ranks your competitors
              and drops your ad acquisition costs at the same time.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#intake" className="btn-primary" data-testid="pb-apply-btn">Apply Now <ArrowUpRight size={18} /></a>
              <a href="#framework" className="btn-ghost">See the 30·30·40 framework</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FRAMEWORK */}
      <section id="framework" className="relative py-24 md:py-32 border-t border-white/5 tl-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">The framework</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              The <em className="gold-shine font-medium not-italic">30·30·40 Framework™</em>.
            </h2>
            <p className="mt-6 text-zinc-400 font-body max-w-2xl">
              Every BrandChilly content calendar is a precise mix of three pillars. Miss one and the whole engine sputters.
            </p>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {framework.map((f, i) => {
              const Icon = f.icon;
              return (
                <StaggerItem key={i}>
                  <div className="luxury-card p-8 h-full" data-testid={`pb-framework-${i}`}>
                    <div className="flex items-center justify-between mb-5">
                      <div className="font-serif-display text-7xl md:text-8xl gold-shine leading-none">{f.pct}</div>
                      <Icon size={22} className="text-red-500" />
                    </div>
                    <div className="font-serif-display text-2xl mt-2">{f.title}</div>
                    <p className="text-zinc-400 mt-3 font-body leading-relaxed">{f.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* HOW IT CONNECTS */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-dark">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <div className="eyebrow mb-5">How it connects to sales</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.1]">
              Your personal brand is the <em className="gold-shine font-medium not-italic">ultimate trust accelerator</em> for your ads.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-zinc-400 font-body leading-relaxed">
              When leads see your ad, then research your founder profile and find high-value content —
              your ad conversion rates jump and acquisition costs drop. That's the compound effect we
              engineer for every client.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SELECTED PB CLIENTS */}
      <section className="relative py-24 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">Selected personal branding clients</div>
            <h2 className="font-serif-display text-4xl md:text-5xl max-w-3xl leading-[1.05]">
              Founders we've put <em className="gold-shine font-medium not-italic">on the map</em>.
            </h2>
          </Reveal>
          <StaggerGroup className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: "CA Nitin Sharma" },
              { name: "The Basic Bird" },
              { name: "Booming Bulls", sub: "Anish Singh Thakur" },
            ].map((c, i) => (
              <StaggerItem key={i}>
                <div className="luxury-card p-8 h-full flex items-center gap-4" data-testid={`pb-client-${i}`}>
                  <div className="w-12 h-12 flex items-center justify-center bg-red-500/15 border border-red-500/30 text-red-400 font-serif-display text-xl flex-shrink-0" style={{ borderRadius: 0 }}>
                    {c.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                  </div>
                  <div>
                    <div className="font-serif-display text-2xl leading-tight">{c.name}</div>
                    {c.sub && <div className="text-sm text-zinc-400 mt-0.5">{c.sub}</div>}
                    <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-600 mt-1">Personal Branding Client</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* CLIENT LOGO MARQUEE */}
      <ClientMarquee eyebrow="And the brands behind us" title="Companies we've worked with." testId="pb-clients" className="tl-alt" />

      {/* INTAKE FORM */}
      <section id="intake" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5 text-center">Personal Branding Application</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-center">
              Apply for the <em className="gold-shine font-medium not-italic">next intake</em>.
            </h2>
            <p className="mt-6 text-zinc-400 font-body text-center max-w-xl mx-auto">
              Limited to a small cohort of founders per month. Tell us about yourself and we'll get back
              within 24 working hours.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="luxury-card p-7 md:p-10 mt-10">
              {submitted ? (
                <div className="py-10 text-center" data-testid="pb-intake-success">
                  <div className="w-14 h-14 mx-auto mb-6 bg-red-500/15 border border-red-500/30 flex items-center justify-center" style={{ borderRadius: 0 }}>
                    <Check size={26} className="text-red-500" />
                  </div>
                  <div className="font-serif-display text-4xl md:text-5xl mb-4">
                    <span className="gold-shine">Application received.</span>
                  </div>
                  <p className="text-zinc-400 max-w-md mx-auto font-body">
                    Thanks — check your inbox for a confirmation. Saurabh and the team will review your profile
                    and reach out within 24 working hours with the next step.
                  </p>
                  <Link to="/book" className="btn-primary mt-8" data-testid="pb-success-book-btn">
                    Or book a strategy call now <ArrowUpRight size={18} />
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="pb-intake-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Name *</label>
                      <input required value={form.name} onChange={update("name")} placeholder="Your full name" className="input-luxury" data-testid="pb-input-name" />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Email *</label>
                      <input required type="email" value={form.email} onChange={update("email")} placeholder="you@brand.com" className="input-luxury" data-testid="pb-input-email" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Phone / WhatsApp *</label>
                    <input required value={form.phone} onChange={update("phone")} placeholder="+91 00000 00000" className="input-luxury" data-testid="pb-input-phone" />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Your active pages / handles + links</label>
                    <textarea rows={3} value={form.handles} onChange={update("handles")} placeholder="e.g. IG: instagram.com/yourhandle  ·  YT: youtube.com/@yourchannel  ·  LinkedIn: …" className="input-luxury resize-none" data-testid="pb-input-handles" />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">What's your goal with personal branding?</label>
                    <textarea rows={3} value={form.goal} onChange={update("goal")} placeholder="e.g. Attract 10 high-ticket clients / month, launch a course, become the go-to voice in my niche…" className="input-luxury resize-none" data-testid="pb-input-goal" />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Hours per day you can commit *</label>
                    <div className="flex flex-wrap gap-2">
                      {HOURS.map((h) => (
                        <button
                          key={h} type="button"
                          onClick={() => setForm({ ...form, hours_per_day: h })}
                          data-testid={`pb-hours-${h}`}
                          className={`px-4 py-2.5 text-sm border transition-all ${form.hours_per_day === h ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
                          style={{ borderRadius: 0 }}
                        >
                          {h} hours
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">
                      The investment is around <span className="text-white">₹75,000</span>. Are you okay with that? *
                    </label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setForm({ ...form, budget_ok: true })} data-testid="pb-budget-yes" className={`px-6 py-2.5 text-sm border transition-all ${form.budget_ok === true ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`} style={{ borderRadius: 0 }}>Yes</button>
                      <button type="button" onClick={() => setForm({ ...form, budget_ok: false })} data-testid="pb-budget-no" className={`px-6 py-2.5 text-sm border transition-all ${form.budget_ok === false ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`} style={{ borderRadius: 0 }}>No</button>
                    </div>
                  </div>

                  <button
                    type="submit" disabled={submitting}
                    className="btn-primary w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="pb-submit-btn"
                  >
                    {submitting ? "Submitting…" : "Submit Application"} <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
